// vg-binding: P7.D-119
// vg-binding: P7.D-120
// vg-binding: P7.D-121
// vg-binding: P7.D-127
// vg-binding: P18.D-10
// vg-binding: P18.D-16
// vg-binding: P18.D-34
//
// apps/vendor/src/pages/SettingsExtended.tsx
//
// Phase 7 Wave 13 Tasks 48/49/50/51/52/53 — Settings shell + 7 tabs.
//
// The Phase 1 settings module already ships under /settings/{profile,
// change-password, preferences, 2fa, billing, security}. This Wave 13
// surface adds the operational tabs (Company / Webhooks / API Keys /
// Staff / Carriers / Notifications / Activity Log) under
// /settings-extended/* so the Phase 1 routes stay untouched.
//
// ---------------------------------------------------------------------------
// Phase 18 Task 01 — the `webhooks` + `api_keys` tabs are SUPERSEDED.
// ---------------------------------------------------------------------------
// Their replacement is the single Integration Settings page (P18.D-21) fed by
// `/api/v1/vendor/integration/*` (P18.D-16). These two tabs stay mounted, with
// their testids and DOM structure intact, because the ACCEPTED e2e specs
// `31-G53-webhook-crud.spec.ts` and `34-G60-api-keys.spec.ts` bind them and
// Phase 18 must not regress an accepted phase.
//
// The storage underneath them has already moved: both tabs now read/write the
// Phase 18 collections (`vendor_integration_keys` / `vendor_webhook_endpoints`)
// through the same deprecated route paths, and revocation funnels through the
// one `revokeKey()` service — so this UI can no longer report a key as live
// after the machine lane has killed it (P18.D-34 revoke-hole).
//
// Do NOT extend these two tabs. Build on the Integration Settings page.

import { csrfFetch } from '../lib/api/csrfFetch';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { cn, PageHeader, useToast } from '@printway/ui';

interface ApiOk<T> { ok: true; data: T }
interface ApiErr { ok: false; error: { code: string; message: string } }
async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) throw new Error(body.error.message);
  return body.data;
}

type TabKey =
  | 'company'
  | 'webhooks'
  | 'api_keys'
  | 'staff'
  | 'carriers'
  | 'notifications'
  | 'activity_log';

const TAB_KEYS: ReadonlyArray<TabKey> = [
  'company',
  'webhooks',
  'api_keys',
  'staff',
  'carriers',
  'notifications',
  'activity_log',
];

const TAB_LABEL_KEY: Record<TabKey, string> = {
  company: 'extended.tabCompany',
  webhooks: 'extended.tabWebhooks',
  api_keys: 'extended.tabApiKeys',
  staff: 'extended.tabStaff',
  carriers: 'extended.tabCarriers',
  notifications: 'extended.tabNotifications',
  activity_log: 'extended.tabActivityLog',
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function CompanyTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const q = useQuery({
    queryKey: ['vendor', 'profile'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/profile', { credentials: 'include' });
      return unwrap<Record<string, unknown>>(res);
    },
  });
  const profile = (q.data ?? {}) as {
    business_name?: string;
    legal_name?: string;
    phone?: string;
    timezone?: string;
    capacity_global?: number;
    vacation_mode?: { enabled?: boolean };
  };
  const qc = useQueryClient();
  const mutate = useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const res = await csrfFetch('/api/v1/vendor/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<{ saved: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'profile'] });
    },
    // fix/vendor-batch-e — surface server rejections (silent-mutation sweep).
    onError: (e: unknown) => {
      toast({
        variant: 'error',
        title: t('extended.companySaveError'),
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.companyTitle')}</h3>
      <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
        <Row label={t('extended.companyBusinessName')} value={profile.business_name ?? '—'} />
        <Row label={t('extended.companyLegalName')} value={profile.legal_name ?? '—'} />
        <Row label={t('extended.companyPhone')} value={profile.phone ?? '—'} />
        <Row label={t('extended.companyTimezone')} value={profile.timezone ?? '—'} />
        <Row label={t('extended.companyCapacityPerDay')} value={String(profile.capacity_global ?? 0)} />
        <Row
          label={t('extended.companyVacationLabel')}
          value={profile.vacation_mode?.enabled ? t('extended.companyVacationOn') : t('extended.companyVacationOff')}
        />
      </dl>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="settings-vacation-toggle"
          onClick={() =>
            mutate.mutate({
              vacation_mode: {
                enabled: !(profile.vacation_mode?.enabled === true),
              },
            })
          }
          disabled={mutate.isPending}
          className="rounded-md border border-pw-border bg-pw-surface px-3 py-1.5 text-sm font-medium text-pw-text hover:bg-pw-bg"
        >
          {profile.vacation_mode?.enabled ? t('extended.companyVacationToggleOff') : t('extended.companyVacationToggleOn')}
        </button>
      </div>
      <p className="text-xs text-pw-text-muted">
        {t('extended.companyVacationHint')}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between border-b border-pw-border py-1">
      <dt className="text-pw-text-muted">{label}</dt>
      <dd className="font-medium text-pw-text">{value}</dd>
    </div>
  );
}

/** @deprecated P18.D-34 — superseded by the Integration Settings page (P18.D-21). */
function WebhooksTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const q = useQuery({
    queryKey: ['vendor', 'webhooks'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/webhooks', { credentials: 'include' });
      return unwrap<{
        rows: Array<{
          webhook_id: string;
          url: string;
          events: string[];
          /**
           * Truthful liveness (P18.D-22): the server ANDs the vendor's on/off
           * intent with `status === 'verified'`, so this is never `true` for
           * an endpoint delivery is actually refusing. Render it as-is — do
           * not re-derive liveness from `status` here.
           */
          is_active: boolean;
          /** P18.D-06 lifecycle. `auto_disabled` is distinct from `unverified`. */
          status: 'unset' | 'unverified' | 'verified' | 'auto_disabled';
          /**
           * Constant mask (`whsec_••••`). Phase 7 leaked the first 8 chars of
           * the PLAINTEXT secret here; the secret is encrypted at rest now and
           * is never derived from for display.
           */
          signing_secret_preview: string;
        }>;
      }>(res);
    },
  });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.webhooksTitle')}</h3>
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('extended.webhooksEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data!.rows.map((w) => (
            <li
              key={w.webhook_id}
              className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm"
            >
              <p className="font-medium text-pw-text">{w.url}</p>
              <p className="text-xs text-pw-text-muted">
                {t('extended.webhooksMeta', {
                  events: w.events.join(', '),
                  active: w.is_active ? t('extended.yes') : t('extended.no'),
                  secret: w.signing_secret_preview,
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** @deprecated P18.D-34 — superseded by the Integration Settings page (P18.D-21). */
function ApiKeysTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['vendor', 'api-keys'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/api-keys', { credentials: 'include' });
      return unwrap<{
        rows: Array<{
          api_key_id: string;
          name: string;
          /**
           * Masked display form of the new `pwv_{keyId}_{secret}` credential
           * (P18.D-12) — the secret half never reaches this table. The list
           * only ever contains keys with `revokedAt === null`, so a revoked
           * key disappears on the next refetch with no TTL lag (E-01).
           */
          prefix: string;
          scopes: string[];
          last_used_at: number | null;
          created_at: number | undefined;
        }>;
      }>(res);
    },
  });
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('products.write');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/api-keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, scopes: scopes.split(',').map((s) => s.trim()) }),
      });
      return unwrap<{ api_key_id: string; secret: string; prefix: string }>(res);
    },
    onSuccess: (r) => {
      setNewSecret(r.secret);
      setName('');
      void qc.invalidateQueries({ queryKey: ['vendor', 'api-keys'] });
    },
    // fix/vendor-batch-e — surface server rejections (silent-mutation sweep).
    onError: (e: unknown) => {
      toast({
        variant: 'error',
        title: t('extended.apiKeysCreateError'),
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await csrfFetch(`/api/v1/vendor/api-keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      return unwrap<{ revoked: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'api-keys'] });
    },
    // fix/vendor-batch-e — surface server rejections (silent-mutation sweep).
    onError: (e: unknown) => {
      toast({
        variant: 'error',
        title: t('extended.apiKeysRevokeError'),
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.apiKeysTitle')}</h3>
      {newSecret ? (
        <p className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-700">
          {t('extended.apiKeysSecretWarn')} <code className="font-mono">{newSecret}</code>
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {(q.data?.rows ?? []).map((k) => (
          <li
            key={k.api_key_id}
            className="flex items-center justify-between rounded-md border border-pw-border bg-pw-surface p-3 text-sm"
          >
            <div>
              <p className="font-medium text-pw-text">{k.name}</p>
              <p className="text-xs text-pw-text-muted">
                {t('extended.apiKeysMeta', {
                  prefix: k.prefix,
                  scopes: k.scopes.join(', '),
                  lastUsed: k.last_used_at
                    ? new Date(k.last_used_at).toLocaleDateString(undefined)
                    : t('extended.apiKeysNeverUsed'),
                })}
              </p>
            </div>
            <button
              type="button"
              data-testid={`api-key-revoke-${k.api_key_id}`}
              onClick={() => revoke.mutate(k.api_key_id)}
              disabled={revoke.isPending}
              className="rounded border border-pw-border bg-pw-bg px-2 py-1 text-xs font-medium text-pw-text-muted hover:bg-red-50 hover:text-red-700"
            >
              {t('extended.apiKeysRevoke')}
            </button>
          </li>
        ))}
      </ul>
      <div className="rounded-md border border-pw-border bg-pw-surface p-3">
        <p className="mb-2 text-sm font-medium text-pw-text">{t('extended.apiKeysCreateTitle')}</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <input
            data-testid="api-key-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('extended.apiKeysNamePlaceholder')}
            className="h-9 rounded border border-pw-border bg-pw-bg px-2 text-sm focus:border-pw-primary focus:outline-none"
          />
          <input
            data-testid="api-key-scopes-input"
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
            placeholder={t('extended.apiKeysScopesPlaceholder')}
            className="h-9 rounded border border-pw-border bg-pw-bg px-2 text-sm focus:border-pw-primary focus:outline-none"
          />
        </div>
        <button
          type="button"
          data-testid="api-key-create-submit"
          onClick={() => create.mutate()}
          disabled={create.isPending || name.trim().length === 0}
          className="mt-2 rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-50"
        >
          {create.isPending ? t('extended.apiKeysCreating') : t('extended.apiKeysCreate')}
        </button>
      </div>
    </div>
  );
}

function StaffTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const q = useQuery({
    queryKey: ['vendor', 'staff-roles'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/staff-roles', { credentials: 'include' });
      return unwrap<{
        rows: Array<{ role_id: string; name: string; permissions: string[]; is_builtin: boolean }>;
        available_permissions: string[];
      }>(res);
    },
  });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.staffTitle')}</h3>
      <p className="text-xs text-pw-text-muted">
        {t('extended.staffPermissionsAvailable', { list: (q.data?.available_permissions ?? []).join(', ') })}
      </p>
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('extended.staffEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data!.rows.map((r) => (
            <li key={r.role_id} className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm">
              <p className="font-medium text-pw-text">
                {r.name} {r.is_builtin ? <span className="text-xs text-pw-text-muted">{t('extended.staffBuiltinTag')}</span> : null}
              </p>
              <p className="text-xs text-pw-text-muted">
                {t('extended.staffPermissionsList', { list: r.permissions.join(', ') })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CarriersTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const q = useQuery({
    queryKey: ['vendor', 'carriers'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/carriers', { credentials: 'include' });
      return unwrap<{
        rows: Array<{
          carrier_id: string;
          name: string;
          carrier_type: 'self_ship' | 'printway_pickup';
          api_key_hint: string | null;
          is_active: boolean;
          label_format: 'pdf' | 'zpl' | 'png';
        }>;
      }>(res);
    },
  });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.carriersTitle')}</h3>
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('extended.carriersEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data!.rows.map((c) => (
            <li key={c.carrier_id} className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm">
              <p className="font-medium text-pw-text">{c.name}</p>
              <p className="text-xs text-pw-text-muted">
                {t('extended.carrierMeta', {
                  type: t(`extended.carrierType_${c.carrier_type}`),
                  format: c.label_format.toUpperCase(),
                  status: c.is_active
                    ? t('extended.carrierActive')
                    : t('extended.carrierInactive'),
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const NOTIF_CATEGORIES = ['orders', 'products', 'payments', 'system'] as const;
const NOTIF_CHANNELS = ['in_app', 'email', 'sms', 'ws'] as const;
type NotifCategory = (typeof NOTIF_CATEGORIES)[number];
type NotifChannel = (typeof NOTIF_CHANNELS)[number];
type NotifCell = Record<NotifChannel, boolean>;
type NotifMatrix = Record<string, NotifCell>;

interface NotifPrefs {
  matrix: NotifMatrix;
  dnd_start: string | null;
  dnd_end: string | null;
}

function emptyNotifCell(): NotifCell {
  return { in_app: false, email: false, sms: false, ws: false };
}

function NotificationsTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['vendor', 'notif-prefs'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/notifications/prefs', {
        credentials: 'include',
      });
      return unwrap<NotifPrefs>(res);
    },
  });

  const [matrix, setMatrix] = useState<NotifMatrix>({});
  const [dndStart, setDndStart] = useState('');
  const [dndEnd, setDndEnd] = useState('');

  useEffect(() => {
    if (!q.data) return;
    const next: NotifMatrix = {};
    for (const cat of NOTIF_CATEGORIES) {
      const cell = q.data.matrix[cat];
      next[cat] = {
        in_app: cell?.in_app ?? false,
        email: cell?.email ?? false,
        sms: cell?.sms ?? false,
        ws: cell?.ws ?? false,
      };
    }
    setMatrix(next);
    setDndStart(q.data.dnd_start ?? '');
    setDndEnd(q.data.dnd_end ?? '');
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/notifications/prefs', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matrix,
          dnd_start: dndStart.trim() === '' ? null : dndStart,
          dnd_end: dndEnd.trim() === '' ? null : dndEnd,
        }),
      });
      return unwrap<{ saved: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'notif-prefs'] });
      toast({ variant: 'success', title: t('extended.notifSaved') });
    },
    onError: (e: unknown) => {
      toast({
        variant: 'error',
        title: t('extended.notifSaveError'),
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });

  const toggle = (cat: NotifCategory, ch: NotifChannel): void => {
    setMatrix((prev) => {
      const cell = prev[cat] ?? emptyNotifCell();
      return { ...prev, [cat]: { ...cell, [ch]: !cell[ch] } };
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.notifTitle')}</h3>
      <p className="text-xs text-pw-text-muted">{t('extended.notifHint')}</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-pw-border text-left text-xs text-pw-text-muted">
              <th className="py-2 pr-4 font-medium">{t('extended.notifCategoryCol')}</th>
              {NOTIF_CHANNELS.map((ch) => (
                <th key={ch} className="px-3 py-2 text-center font-medium">
                  {t(`extended.notifChannel_${ch}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NOTIF_CATEGORIES.map((cat) => {
              const cell = matrix[cat] ?? emptyNotifCell();
              return (
                <tr key={cat} className="border-b border-pw-border">
                  <td className="py-2 pr-4 font-medium text-pw-text">
                    {t(`extended.notifCategory_${cat}`)}
                  </td>
                  {NOTIF_CHANNELS.map((ch) => (
                    <td key={ch} className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`notif-${cat}-${ch}`}
                        checked={cell[ch]}
                        onChange={() => toggle(cat, ch)}
                        className="h-4 w-4 cursor-pointer accent-pw-primary"
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="notif-dnd-start" className="text-xs font-medium text-pw-text-muted">
            {t('extended.notifDndStart')}
          </label>
          <input
            id="notif-dnd-start"
            type="time"
            data-testid="notif-dnd-start"
            value={dndStart}
            onChange={(e) => setDndStart(e.target.value)}
            className="h-9 rounded border border-pw-border bg-pw-bg px-2 text-sm focus:border-pw-primary focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="notif-dnd-end" className="text-xs font-medium text-pw-text-muted">
            {t('extended.notifDndEnd')}
          </label>
          <input
            id="notif-dnd-end"
            type="time"
            data-testid="notif-dnd-end"
            value={dndEnd}
            onChange={(e) => setDndEnd(e.target.value)}
            className="h-9 rounded border border-pw-border bg-pw-bg px-2 text-sm focus:border-pw-primary focus:outline-none"
          />
        </div>
      </div>

      <div>
        <button
          type="button"
          data-testid="notif-save"
          onClick={() => save.mutate()}
          disabled={save.isPending || q.isLoading}
          className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-50"
        >
          {save.isPending ? t('extended.notifSaving') : t('extended.notifSave')}
        </button>
      </div>
    </div>
  );
}

function ActivityLogTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const q = useQuery({
    queryKey: ['vendor', 'activity-log'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/activity-log?limit=50', { credentials: 'include' });
      return unwrap<{
        rows: Array<{
          log_id: string;
          actor_user_id: string | null;
          action: string;
          resource_type: string;
          resource_id: string | null;
          occurred_at: number;
        }>;
        next_cursor: string | null;
      }>(res);
    },
  });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-pw-text">{t('extended.activityTitle')}</h3>
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('extended.activityEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {q.data!.rows.map((r) => (
            <li key={r.log_id} className="text-xs text-pw-text-muted">
              <span className="font-mono">{new Date(r.occurred_at).toLocaleString(undefined)}</span>
              {' · '}
              <span className="text-pw-text">{r.action}</span>
              {' · '}
              {r.resource_type}
              {r.resource_id ? `#${r.resource_id.slice(-8)}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function SettingsExtendedPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const [tab, setTab] = useState<TabKey>('company');

  return (
    <main
      data-testid="settings-extended-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader title={t('extended.pageTitle')} />

      <nav className="flex w-full items-center gap-1 overflow-x-auto border-b border-pw-border">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`settings-tab-${key}`}
            onClick={() => setTab(key)}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              key === tab
                ? 'border-pw-primary text-pw-primary-strong'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            {t(TAB_LABEL_KEY[key])}
          </button>
        ))}
      </nav>

      <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
        {tab === 'company' ? <CompanyTab /> : null}
        {tab === 'webhooks' ? <WebhooksTab /> : null}
        {tab === 'api_keys' ? <ApiKeysTab /> : null}
        {tab === 'staff' ? <StaffTab /> : null}
        {tab === 'carriers' ? <CarriersTab /> : null}
        {tab === 'notifications' ? <NotificationsTab /> : null}
        {tab === 'activity_log' ? <ActivityLogTab /> : null}
      </section>
    </main>
  );
}

export default SettingsExtendedPage;
